import { ReactNode } from "react";
import { Icon, RadioButton, Tooltip, type RadioButtonProps } from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { useController, useFormContext } from "react-hook-form";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormRadioButtonProps extends Omit<RadioButtonProps, "checked" | "onChange"> {
    name: string;
    value: string;
    onChangeHook?: (val: string) => void;
    /** Override the write entirely — receives the raw value + the underlying field, so callers can set multiple fields atomically instead of the default single setValue(name, value). */
    onChange?: (value: string, field: ReturnType<typeof useController>["field"]) => void;
    /** Override the checked computation (default: field.value === value) — needed when the underlying field isn't a string enum, e.g. a boolean toggled by two radios. */
    checked?: boolean;
    disabled?: boolean;
    /** Shows an info icon next to the label with this content on hover/tap. */
    tooltipContent?: ReactNode;
}

export function FormRadioButton({
    name,
    value,
    label,
    tooltipContent,
    onChangeHook,
    onChange,
    checked,
    disabled,
    ...rest
}: FormRadioButtonProps) {
    const { control, setValue } = useFormContext();

    const { field } = useController({ control, name: name as any });

    const isAbDisabled = useVariantFieldDisabled(name);

    return (
        <RadioButton
            {...rest}
            label={
                tooltipContent ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {label}
                        <Tooltip content={tooltipContent} width="wide">
                            <Icon source={InfoIcon} tone="subdued" />
                        </Tooltip>
                    </div>
                ) : (
                    label
                )
            }
            disabled={disabled || isAbDisabled}
            checked={checked !== undefined ? checked : field.value === value}
            onChange={() => {
                if (onChange) {
                    onChange(value, field);
                    return;
                }
                setValue(name as any, value as any, { shouldDirty: true, shouldValidate: true });
                if (onChangeHook) {
                    onChangeHook(value);
                }
            }}
        />
    );
}
